import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { TrendingUp, Receipt, Armchair, Activity, Headphones, ClipboardList } from 'lucide-react';
import { sortTablesByNumber } from '../lib/tableSort';
import { OnboardingChecklist } from '../components/OnboardingChecklist';

const Kpi = ({ icon: Icon, label, value, color, testId }) => (
    <div className="panel" data-testid={testId}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
            <div className="kicker">{label}</div>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: `${color}1f`, border: `1px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon size={16} color={color} />
            </div>
        </div>
        <div className="headline" style={{ fontSize: '2.2rem', color: '#fff' }}>{value}</div>
    </div>
);

const STATUS_LABEL = { free: 'Liberă', occupied: 'Ocupată', reserved: 'Rezervată' };

function WaiterDashboard({ data, userName }) {
    const tables = data?.assigned_tables || [];
    const zones = useMemo(() => [...new Set(tables.map(t => t.zone))], [tables]);

    return (
        <>
            <div className="kicker">Dashboard Ospătar</div>
            <h1 className="headline" style={{ fontSize: '2.6rem', margin: '0.3rem 0 0.4rem' }}>
                Bună, <em style={{ color: 'var(--amber)' }}>{userName}</em>!
            </h1>
            <p style={{ color: 'var(--text-muted)', margin: '0 0 1.6rem' }}>
                Acestea sunt mesele tale asignate. Vezi comenzile active din pagina Comenzi sau Vedere Ospătar.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.4rem' }}>
                <Kpi icon={Armchair} label="Mese asignate" value={data?.my_tables_count ?? 0} color="#F59E0B" testId="kpi-my-tables" />
                <Kpi icon={Activity} label="Mese ocupate" value={data?.my_occupied_tables ?? 0} color="#00D9FF" testId="kpi-my-occupied" />
                <Kpi icon={Receipt} label="Comenzi active" value={data?.active_orders ?? 0} color="#FF2E92" testId="kpi-my-orders" />
                <Kpi icon={TrendingUp} label="Comenzi azi" value={data?.today_orders ?? 0} color="#14F195" testId="kpi-my-today" />
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1.2rem' }}>
                <Link to="/r/waiter" className="btn btn-primary btn-sm" data-testid="goto-waiter-view">
                    <Headphones size={14} /> Vedere Ospătar
                </Link>
                <Link to="/r/orders" className="btn btn-secondary btn-sm" data-testid="goto-orders">
                    <ClipboardList size={14} /> Toate comenzile mele
                </Link>
            </div>

            {!tables.length ? (
                <div className="panel" style={{ textAlign: 'center', color: 'var(--text-muted)' }} data-testid="no-assigned-tables">
                    Nu ai mese asignate încă. Cere managerului să te repartizeze din <strong style={{ color: 'var(--amber)' }}>Mese & QR</strong>.
                </div>
            ) : (
                <>
                    <div style={{ display: 'flex', gap: 12, marginBottom: '1rem', flexWrap: 'wrap' }}>
                        <div className="badge badge-muted">Liberă</div>
                        <div className="badge badge-amber">Ocupată</div>
                        <div className="badge badge-cyan">Rezervată</div>
                    </div>
                    {zones.map(zone => (
                        <div key={zone} style={{ marginBottom: '1.6rem' }} data-testid={`waiter-zone-${zone}`}>
                            <h2 className="headline" style={{ fontSize: '1.35rem', margin: '0 0 0.8rem' }}>{zone}</h2>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.8rem' }}>
                                {sortTablesByNumber(tables.filter(t => t.zone === zone)).map(t => (
                                    <div key={t.id} className={`table-cell table-cell-${t.status}`} data-testid={`waiter-table-${t.id}`}>
                                        <div className="headline" style={{ fontSize: '2rem' }}>{t.number}</div>
                                        <div className="kicker" style={{ fontSize: '0.62rem' }}>{t.capacity} pers.</div>
                                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                                            {STATUS_LABEL[t.status] || t.status}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </>
            )}
        </>
    );
}

export default function Overview() {
    const { user, loading: authLoading } = useAuth();
    const [data, setData] = useState(null);
    const isWaiter = user?.role === 'waiter';
    const tenantReady = Boolean(user?.restaurant_id) && !authLoading;

    useEffect(() => {
        if (!tenantReady) return undefined;
        const load = () => api.get('/r/overview').then(r => setData(r.data)).catch(() => {});
        load();
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [tenantReady]);

    if (isWaiter) {
        return (
            <div className="fade-in-up" data-testid="overview-page">
                <WaiterDashboard data={data} userName={user?.name?.split(' ')[0] || 'Ospătar'} />
            </div>
        );
    }

    const canSeeOnboarding = ['restaurant_admin', 'manager'].includes(user?.role);

    return (
        <div className="fade-in-up" data-testid="overview-page">
            <div className="kicker">Dashboard</div>
            <h1 className="headline" style={{ fontSize: '2.6rem', margin: '0.3rem 0 1.6rem' }}>Bună ziua! Iată ce se întâmplă <em style={{ color: 'var(--amber)' }}>chiar acum</em>.</h1>

            {canSeeOnboarding && (
                <div style={{ marginBottom: '1.4rem' }}>
                    <OnboardingChecklist enabled={tenantReady} />
                </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.4rem' }}>
                <Kpi icon={Receipt} label="Comenzi azi" value={data?.today_orders ?? '—'} color="#F59E0B" testId="kpi-orders" />
                <Kpi icon={TrendingUp} label="Venituri azi (RON)" value={data ? (data.today_revenue || 0).toFixed(0) : '—'} color="#14F195" testId="kpi-revenue" />
                <Kpi icon={Armchair} label="Mese ocupate" value={data ? `${data.occupied_tables}/${data.total_tables}` : '—'} color="#00D9FF" testId="kpi-tables" />
                <Kpi icon={Activity} label="Comenzi active" value={data?.active_orders ?? '—'} color="#FF2E92" testId="kpi-active" />
            </div>

            <div className="panel">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.6rem' }}>
                    <span className="live-dot" />
                    <div className="kicker">Live</div>
                </div>
                <p style={{ margin: 0, color: 'var(--text-muted)' }}>
                    Notificările apar instant când o comandă nouă intră, când bucătăria termină un produs sau când un client cere ajutor. Folosește pagina <strong style={{ color: 'var(--amber)' }}>Comenzi</strong> și <strong style={{ color: 'var(--amber)' }}>KDS</strong> pentru a procesa fluxul.
                </p>
            </div>
        </div>
    );
}
